import { useEffect, useState } from 'react';
import { DataGrid } from '../../components/grid/DataGrid';
import { NuovoPreventivo } from './NuovoPreventivo';
import { SchedaPreventivo } from './SchedaPreventivo';
import { usePuo } from '../sessione/ContestoSessione';
import { apri, chiudi, idDallAncora } from '../../lib/ancora';

const INTESTAZIONI: Record<string, string> = {
  Numero: 'Codice',
  DataPreventivo: 'Redatto il',
  DataInvio: 'Inviato il',
  DataValidita: 'Valido fino',
  PrezziBloccati: 'Bloccato',
  Oggetto: 'Oggetto / descrizione interna',
};

/*  Due colonne dichiarate che in elenco non si vedono.

    La REVISIONE perche' il numero la dice gia': dalla prima in poi si legge «26-0001/1»,
    e una colonna che ripete «1» accanto e' un numero da leggere due volte per sapere la
    stessa cosa.

    Il margine percentuale NON sta in elenco: deciso in riunione il 14/09. Dentro la
    scheda resta, ed e' il posto giusto — e' un numero su cui si ragiona, non uno che si
    scorre. La colonna resta dichiarata nel registro delle griglie: toglierla di la'
    vorrebbe dire non poterci piu' nemmeno filtrare, e chi cerca «le offerte sotto il
    dieci per cento» la usa.  */
/*  «Controllato» in elenco dice a colpo d'occhio quali offerte sono pronte per uscire:
    e' la colonna che si guarda prima di mandare. Il tecnico responsabile resta fra le
    colonne dichiarate ma fuori da qui — su un lavoro grosso e' un'altra persona ancora,
    e chi ci filtra puo' aggiungerla.  */
const COLONNE = [
  'Numero',
  'Sede',
  'Cliente',
  'Oggetto',
  'DataPreventivo',
  'DataInvio',
  'DataValidita',
  'Redattore',
  'Controllato',
  'Totale',
  'Stato',
  'PrezziBloccati',
];

/*  Lo stato si scorre, non si legge: a pastiglia colorata si vede quale riga e' ancora
    in casa, quale e' fuori in attesa e quale ha prodotto una commessa senza fermarsi su
    ogni parola. I colori sono quelli del ciclo di vita dentro la scheda — chi filtra qui
    e poi apre li' deve trovare lo stesso verde.  */
const PASTIGLIE = ['Stato'];

export function ElencoPreventivi() {
  const [aperto, setAperto] = useState<string | null>(() => idDallAncora('preventivi'));

  /*  Un'ancora con dentro un identificativo apre quel documento: e' cosi' che
      arriva chi ha premuto il collegamento su un'altra scheda, o chi ha incollato
      un indirizzo ricevuto in una mail. Si legge anche dopo, perche' il
      collegamento puo' arrivare quando la schermata e' gia' aperta e il tasto
      «indietro» del browser deve riportare dov'era.  */
  useEffect(() => {
    const rileggi = () => {
      const id = idDallAncora('preventivi');
      if (id !== null) setAperto(id);
    };

    window.addEventListener('hashchange', rileggi);
    return () => window.removeEventListener('hashchange', rileggi);
  }, []);
  const [versione, setVersione] = useState(0);
  const [creando, setCreando] = useState(false);
  const puoFare = usePuo();

  return (
    <section className="pagina con-scheda">
      <div className="colonna-principale">
        <header className="testata-pagina">
          <div>
            <h1>Preventivi</h1>
            <p>
              Apri una riga per costruire il preventivo: capitoli e articoli, ore di posa,
              spese accessorie e cantiere. Il prezzo esce dai costi più il ricarico, e il
              margine è calcolato sul prezzo — «venti per cento» vuol dire venti su cento
              incassati, non venti su cento spesi.
            </p>
            <p className="nota">
              Le trasferte non si scrivono a mano: si calcolano dalle ore del preventivo,
              dai chilometri del cantiere e da quante persone ci vanno. Restano correggibili
              — se si parte in furgone insieme i viaggi sono meno — ma il conto si vede
              smontato, così si può discutere invece di subirlo.
            </p>
          </div>

          {/*  Il bottone c'è solo per chi può redigere: uno che dà 403 è peggio di uno
               che non c'è, perché insegna che l'applicativo dice bugie.  */}
          {puoFare('RedigiPreventivi') && !creando && (
            <button type="button" className="primario" onClick={() => setCreando(true)}>
              Nuovo preventivo
            </button>
          )}
        </header>

        {/*  `versione` e non `key`: cambiare la key RIMONTA la griglia da zero, e una
             griglia che rinasce perde i filtri, l'ordinamento e la pagina su cui si era.
             Chi aveva ristretto l'elenco a un cliente e aggiunge una riga al preventivo
             si ritrovava l'elenco intero. La prop `versione` esiste per questo — rilegge
             le righe e lascia stare il resto — e le altre schermate la usavano gia': qui
             era rimasta la scorciatoia.  */}
        <DataGrid
          versione={versione}
          nomeGriglia="preventivi"
          colonneVisibili={COLONNE}
          intestazioni={INTESTAZIONI}
          pastiglie={PASTIGLIE}
          idSelezionato={aperto}
          onRigaSelezionata={(riga) => {
            const id = riga['Id'];
            if (typeof id !== 'string') return;

            /*  L'indirizzo segue quello che si ha davanti. Prima non lo faceva: la rotta
                `#preventivi/<id>` esisteva e funzionava in lettura — e' cosi' che arriva
                chi preme il collegamento da una commessa — ma nessuno la SCRIVEVA. Quindi
                F5 riportava all'elenco, e il collegamento a un preventivo si poteva
                ricevere e non produrre. Lo stato lo mette comunque l'ascoltatore di
                hashchange, ma lo mettiamo anche qui: cosi' la scheda si apre subito e non
                al giro dopo.  */
            setAperto(id);
            apri('preventivi', id);
          }}
        />
      </div>

      {/*  La griglia si ricarica quando il preventivo cambia: totale e margine sono
           colonne dell'elenco, e lasciarle ferme mentre la scheda accanto mostra numeri
           nuovi è il modo più rapido di non fidarsi più né dell'una né dell'altra.  */}
      {creando && (
        <NuovoPreventivo
          onAnnulla={() => setCreando(false)}
          onCreato={(id) => {
            setCreando(false);
            setAperto(id);
            apri('preventivi', id);
            setVersione((v) => v + 1);
          }}
        />
      )}

      {!creando && aperto && (
        <SchedaPreventivo
          preventivoId={aperto}
          onChiudi={() => {
            setAperto(null);
            chiudi('preventivi');
          }}
          onCambiato={() => setVersione((v) => v + 1)}
          /*  La revisione è una copia: nasce un documento nuovo, e da lì in poi si lavora
              su quello. Si sposta anche l'indirizzo, così F5 riapre la revisione e non
              l'originale.  */
          onSostituito={(nuovo) => {
            setAperto(nuovo);
            apri('preventivi', nuovo);
            setVersione((v) => v + 1);
          }}
        />
      )}
    </section>
  );
}
